/**
 * Shared lattice indexing for grid init + voxel topologies (DESIGNS-TRACK-Y).
 * Linearization: ix fastest — i = ix + nx*iy + nx*ny*iz
 */

export type GridShape = {
  nx: number;
  ny: number;
  nz: number;
  dim: 2 | 3;
  /** Active particle count (may be < capacity). */
  n: number;
  /** nx*ny*nz >= n */
  capacity: number;
};

function ceilSqrt(n: number): number {
  return Math.max(1, Math.ceil(Math.sqrt(n)));
}

/** Factor c into nx>=ny>=nz with nx*ny*nz=c, minimizing axis imbalance. */
function factorCapacity3(c: number): [number, number, number] {
  if (c <= 1) return [1, 1, 1];
  // Perfect cube fast path
  const k = Math.round(Math.cbrt(c));
  if (k * k * k === c) return [k, k, k];

  let best: [number, number, number] = [c, 1, 1];
  let bestScore = Infinity;
  let nz = Math.max(1, Math.floor(Math.cbrt(c)));
  while (nz >= 1) {
    if (c % nz === 0) {
      const rest = c / nz;
      let ny = Math.max(1, Math.floor(Math.sqrt(rest)));
      while (ny >= 1) {
        if (rest % ny === 0) {
          const nx = rest / ny;
          const axes = [nx, ny, nz].sort((a, b) => b - a) as [
            number,
            number,
            number,
          ];
          const [a, b, d] = axes;
          const score = (a - b) ** 2 + (b - d) ** 2 + (d - a) ** 2;
          if (
            score < bestScore ||
            (score === bestScore &&
              (a > best[0] || (a === best[0] && b > best[1])))
          ) {
            bestScore = score;
            best = [a, b, d];
          }
        }
        ny--;
      }
    }
    nz--;
  }
  return best;
}

/**
 * Deterministic grid shape with capacity >= n.
 * dim=2: nx=ceil(sqrt(n)), ny=ceil(n/nx), nz=1
 * dim=3: search capacity c>=n near a cube; factor into nx>=ny>=nz
 */
export function autoGridShape(n: number, dim: 2 | 3): GridShape {
  const nn = Math.max(1, Math.floor(n));
  if (dim === 2) {
    const nx = ceilSqrt(nn);
    const ny = Math.max(1, Math.ceil(nn / nx));
    return { nx, ny, nz: 1, dim: 2, n: nn, capacity: nx * ny };
  }

  // Perfect cube
  const k = Math.round(Math.cbrt(nn));
  if (k * k * k === nn) {
    return { nx: k, ny: k, nz: k, dim: 3, n: nn, capacity: nn };
  }

  const c0 = Math.max(1, Math.ceil(Math.cbrt(nn)));
  const cMax = Math.max(c0 + nn, c0 * 4);
  let bestC = nn;
  let bestAxes: [number, number, number] = [nn, 1, 1];
  let bestScore = Infinity;

  for (let c = Math.max(nn, c0); c <= cMax; c++) {
    const axes = factorCapacity3(c);
    const [nx, ny, nz] = axes;
    if (nx * ny * nz !== c) continue;
    const score =
      (nx - ny) ** 2 + (ny - nz) ** 2 + (nz - nx) ** 2 + (c - nn) * 0.01;
    if (
      score < bestScore ||
      (score === bestScore && c < bestC) ||
      (score === bestScore && c === bestC && nx > bestAxes[0])
    ) {
      bestScore = score;
      bestC = c;
      bestAxes = axes;
    }
    // Early exit on perfect-ish box with c==n
    if (c === nn && score <= 2) break;
  }

  const [nx, ny, nz] = bestAxes;
  return { nx, ny, nz, dim: 3, n: nn, capacity: nx * ny * nz };
}

export function indexToIjk(
  i: number,
  shape: GridShape,
): [number, number, number] {
  const { nx, ny } = shape;
  const ix = i % nx;
  const t = Math.floor(i / nx);
  const iy = t % ny;
  const iz = Math.floor(t / ny);
  return [ix, iy, iz];
}

/** Map lattice coords → particle index, or -1 if OOB / hole (i >= n). */
export function ijkToIndex(
  ix: number,
  iy: number,
  iz: number,
  shape: GridShape,
): number {
  const { nx, ny, nz, n } = shape;
  if (ix < 0 || iy < 0 || iz < 0 || ix >= nx || iy >= ny || iz >= nz) return -1;
  const i = ix + nx * (iy + ny * iz);
  if (i < 0 || i >= n) return -1;
  return i;
}

/** Face-neighbor offsets (4 in 2D, 6 in 3D). */
export function faceOffsets(dim: 2 | 3): [number, number, number][] {
  const o: [number, number, number][] = [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
  ];
  if (dim >= 3) {
    o.push([0, 0, 1], [0, 0, -1]);
  }
  return o;
}

/** Moore neighborhood (8 in 2D, 26 in 3D) excluding self. */
export function mooreOffsets(dim: 2 | 3): [number, number, number][] {
  const out: [number, number, number][] = [];
  const zRange = dim >= 3 ? [-1, 0, 1] : [0];
  for (const dz of zRange) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0 && dz === 0) continue;
        out.push([dx, dy, dz]);
      }
    }
  }
  return out;
}

/** World coordinate along one axis for lattice index k of nside. */
export function mapAxis(k: number, nside: number, extent: number): number {
  if (nside <= 1) return 0;
  const t = k / (nside - 1);
  return -extent + 2 * extent * t;
}
